import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';

import { getActionOptions, getConditionOptions } from './automationHelper';
import { getAutomationActionTypes } from './constants';
import {
    MESSAGE_CONDITION_VALUES,
    PRIORITY_CONDITION_VALUES,
} from 'dashboard/constants/automation';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default function useAutomationValues() {
    const getters = useStoreGetters();
    const { t } = useI18n();
    const agents = useMapGetter('agents/getAgents');
    const campaigns = useMapGetter('campaigns/getAllCampaigns');
    const contacts = useMapGetter('contacts/getContacts');
    const inboxes = useMapGetter('inboxes/getInboxes');
    const labels = useMapGetter('labels/getLabels');
    const teams = useMapGetter('teams/getTeams');
    const slaPolicies = useMapGetter('sla/getSLA');

    const booleanFilterOptions = computed(() => [
        { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
        { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
    ]);

    const statusFilterItems = computed(() => ({
        open: {
            TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
        },
        resolved: {
            TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
        },
        pending: {
            TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
        },
        snoozed: {
            TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
        },
        all: {
            TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
        },
    }));

    const statusFilterOptions = computed(() => {
        const statusFilters = statusFilterItems.value;
        return [
            ...Object.keys(statusFilters).map(status => ({
                id: status,
                name: statusFilters[status].TEXT,
            })),
            { id: 'all', name: t('CHAT_LIST.FILTER_ALL') },
        ];
    });

    const messageTypeOptions = computed(() =>
        MESSAGE_CONDITION_VALUES.map(item => ({
            id: item.id,
            // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
            name: t(`AUTOMATION.MESSAGE_TYPES.${item.i18nKey}`),
        }))
    );

    const priorityOptions = computed(() =>
        PRIORITY_CONDITION_VALUES.map(item => ({
            id: item.id,
            // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
            name: t(`AUTOMATION.PRIORITY_TYPES.${item.i18nKey}`),
        }))
    );

    const availabilityStatusOptions = computed(() =>
        AVAILABILITY_STATUS_KEYS.map(status => ({
            id: status,
            // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
            name: t(
                `PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.${status.toUpperCase()}`
            ),
        }))
    );

    const addNoneToList = list => [
        {
            id: 'nil',
            name: t('AUTOMATION.NONE_OPTION') || 'None',
        },
        ...(list || []),
    ];

    const getConditionDropdownValues = type => {
        return getConditionOptions({
            agents: agents.value,
            booleanFilterOptions: booleanFilterOptions.value,
            campaigns: campaigns.value,
            contacts: contacts.value,
            customAttributes: getters['attributes/getAttributes'].value,
            inboxes: inboxes.value,
            labels: labels.value,
            statusFilterOptions: statusFilterOptions.value,
            availabilityStatusOptions: availabilityStatusOptions.value,
            priorityOptions: priorityOptions.value,
            messageTypeOptions: messageTypeOptions.value,
            teams: teams.value,
            languages,
            countries,
            type,
        });
    };

    const getActionDropdownValues = type => {
        return getActionOptions({
            agents: agents.value,
            inboxes: inboxes.value,
            labels: labels.value,
            teams: teams.value,
            slaPolicies: slaPolicies.value,
            languages,
            type,
            addNoneToListFn: addNoneToList,
            priorityOptions: priorityOptions.value,
            actionTypes: getAutomationActionTypes(),
        });
    };

    return {
        booleanFilterOptions,
        statusFilterItems,
        statusFilterOptions,
        availabilityStatusOptions,
        priorityOptions,
        messageTypeOptions,
        getConditionDropdownValues,
        getActionDropdownValues,
        agents,
        campaigns,
        contacts,
        inboxes,
        labels,
        teams,
        slaPolicies,
    };
}
